import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { windowText } from "../components/Countdown";
import { money, customText } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, academy } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="h-14 w-14 text-zinc-700 mx-auto" />
        <h1 className="font-heading font-black uppercase text-3xl text-white mt-6">Your Cart Is Empty</h1>
        <p className="text-zinc-500 mt-2">Add some official Paragon gear to get started.</p>
        <Link to="/shop" className="btn-primary inline-block px-8 py-3.5 rounded-sm mt-6">Shop Paragon</Link>
      </div>
    );

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-8 py-10">
      <h1 className="font-heading font-black uppercase text-4xl text-white mb-8">Your Cart</h1>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((it, i) => (
            <div key={i} data-testid={`cart-item-${i}`} className="flex gap-4 bg-[#121216] border border-white/10 rounded-lg p-4">
              <img src={it.image} alt={it.name} className="h-20 w-28 sm:h-24 sm:w-36 rounded-md object-contain bg-[#0A0A0C] shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 data-testid={`cart-item-name-${i}`} className="font-heading font-bold text-white text-lg leading-tight">{it.name}</h4>
                <p data-testid={`cart-item-variant-${i}`} className="font-mono text-xs text-zinc-500 mt-1">
                  {it.color ? `${it.color} · ` : ""}Size {it.size}
                </p>
                {customText(it.customization) && <p data-testid={`cart-item-custom-${i}`} className="font-mono text-xs text-yellow-400 mt-1">{customText(it.customization)}</p>}
                <p className="font-mono text-xs text-zinc-400 mt-1"><span data-testid={`cart-item-unit-${i}`}>{money(it.unit_price)}</span> each</p>
                <p data-testid={`cart-item-total-${i}`} className="font-mono text-yellow-400 font-bold mt-0.5">{money(it.unit_price * it.qty)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button data-testid={`cart-remove-${i}`} onClick={() => removeItem(i)} className="text-zinc-500 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
                <div className="flex items-center border border-white/15 rounded-sm">
                  <button data-testid={`cart-qty-dec-${i}`} onClick={() => updateQty(i, it.qty - 1)} className="p-2 text-zinc-300 hover:text-yellow-400"><Minus className="h-3.5 w-3.5" /></button>
                  <span data-testid={`cart-qty-${i}`} className="w-8 text-center font-mono text-sm">{it.qty}</span>
                  <button data-testid={`cart-qty-inc-${i}`} onClick={() => updateQty(i, it.qty + 1)} className="p-2 text-zinc-300 hover:text-yellow-400"><Plus className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#121216] border border-white/10 rounded-lg p-6 h-fit">
          <h3 className="font-heading font-bold uppercase text-white text-xl mb-4">Order Summary</h3>
          {academy && (
            <p className="text-xs font-mono text-zinc-400 mb-3">
              Academy: <span className="text-emerald-400">{academy.name}</span>
            </p>
          )}
          <div className="flex justify-between text-zinc-300 mb-2"><span>Subtotal</span><span data-testid="cart-subtotal" className="font-mono">{money(subtotal)}</span></div>
          <div className="flex justify-between text-zinc-500 text-sm mb-2"><span>Shipping</span><span className="font-mono">Calculated at checkout</span></div>
          <div className="flex justify-between text-zinc-500 text-sm mb-2"><span>Sales tax</span><span className="font-mono">CA orders, at checkout</span></div>
          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded p-3 my-4 text-xs font-mono text-yellow-300">
            ● Preorder · Winter '26 · Made for this order window, not shipped immediately. {windowText()}
          </div>
          <Link to="/shop" data-testid="cart-continue-shopping" className="block text-center text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-yellow-400 mb-3">← Continue Shopping</Link>
          <button data-testid="checkout-btn" onClick={() => navigate("/checkout")} className="btn-primary w-full px-6 py-3.5 rounded-sm">
            Checkout
          </button>
        </div>
      </div>
    </div>
  );
}
